import type { TaskDto } from '@schwrz/shared';
import type { RouteLocationRaw } from 'vue-router';

import { companyDetailsParam, type CompanyRouteRef } from './company-route';
import { projectApiParam, type ProjectRouteRef } from './project-route';

/** Minimal task identity for building task URLs. */
export type TaskRouteRef = { number: number; id?: string } | { id: string; number?: number };

export type TaskDetailsParams = {
  company: CompanyRouteRef;
  project: ProjectRouteRef;
  task: TaskRouteRef;
};

/** Public task segment — numeric id when present, else legacy UUID. */
export function taskDetailsParam(task: TaskRouteRef): string {
  if ('number' in task && task.number !== undefined && task.number !== null) {
    return String(task.number);
  }
  if ('id' in task && task.id) return task.id;
  throw new Error('Task route ref must include number or id');
}

/** Build /:companyId/:projectId/:taskId using public numeric ids. */
export function taskDetailsTo(
  params: TaskDetailsParams,
  query?: Record<string, string | undefined>,
): RouteLocationRaw {
  return {
    name: 'task-details',
    params: {
      companyId: companyDetailsParam(params.company),
      projectId: projectApiParam(params.project),
      taskId: taskDetailsParam(params.task),
    },
    ...(query ? { query } : {}),
  };
}

type ProjectLookupRow = ProjectRouteRef & { id: string; clientId: string };

/** Resolve company + project for a task row already loaded in Pinia stores. */
export function taskDetailsToForTask(
  task: Pick<TaskDto, 'projectId'> & TaskRouteRef,
  lookup: {
    project: (projectId: string) => ProjectLookupRow | undefined;
    company: (clientId: string) => CompanyRouteRef | undefined;
  },
  query?: Record<string, string | undefined>,
): RouteLocationRaw | null {
  const project = lookup.project(task.projectId);
  if (!project) return null;
  const company = lookup.company(project.clientId);
  if (!company) return null;
  return taskDetailsTo({ company, project, task }, query);
}
